import React, { useState, useEffect, useCallback } from 'react';
import Loader from '../../common/Loader.js';
import Alert from '../../common/Alert.js';
import whatsappPaymentService from '../../../services/WhatsAppPaymentService.js';
import { X, DollarSign, Download, Calendar, CreditCard, FileText, Zap } from "lucide-react";
import Button from '../../common/Button.js';


const ViewPaymentModal = ({ paymentId, onClose, onEdit }) => {
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);

  const fetchPayment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await whatsappPaymentService.getPaymentById(paymentId);
      setPayment(response.data);
    } catch (err) {
      console.error('Error fetching payment:', err);
      Alert.error('Failed to load payment details');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [paymentId, onClose]);

  useEffect(() => {
    fetchPayment();
  }, [fetchPayment]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodIcon = (method) => {
    const iconClass = "w-5 h-5";
    switch (method?.toLowerCase()) {
      case 'bank_transfer':
        return <CreditCard className={iconClass} />;
      case 'cheque':
        return <FileText className={iconClass} />;
      case 'upi':
        return <Zap className={iconClass} />;
      case 'online':
        return <Download className={iconClass} />;
      default:
        return <DollarSign className={iconClass} />;
    }
  };

  const getPaymentMethodDisplay = (method) => {
    const methodMap = {
      'bank_transfer': 'Bank Transfer',
      'cheque': 'Cheque',
      'upi': 'UPI',
      'online': 'Online',
      'cash': 'Cash',
      'other': 'Other'
    };
    return methodMap[method?.toLowerCase()] || method || 'Unknown';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusDisplayName = (status) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase() || 'Unknown';
  };

  const handleDownloadReceipt = () => {
    if (payment?.receipt_url) {
      window.open(payment.receipt_url, '_blank');
    } else {
      Alert.error('Receipt not available');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <Loader size="default" message="Loading payment details..." />
        </div>
      </div>
    );
  }

  if (!payment) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Payment Details</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Amount Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium mb-1">Payment Amount</p>
            <p className="text-3xl font-bold text-blue-900">{formatCurrency(payment.amount)}</p>
          </div>

          {/* Status Badge */}
          <div>
            <p className="text-xs text-gray-600 font-medium mb-2">Status</p>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(payment.payment_status)}`}>
              {getStatusDisplayName(payment.payment_status)}
            </span>
          </div>

          {/* Payment Information */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-600 font-medium">Payment Date</p>
                <p className="text-sm text-gray-900 font-medium">{formatDate(payment.payment_date)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-600 font-medium">Payment Method</p>
                <div className="flex items-center gap-2">
                  {getPaymentMethodIcon(payment.payment_method)}
                  <p className="text-sm text-gray-900 font-medium">{getPaymentMethodDisplay(payment.payment_method)}</p>
                </div>
              </div>
            </div>

            {payment.transaction_id && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-600 font-medium">Transaction ID</p>
                  <p className="text-sm text-gray-900 font-medium break-all">{payment.transaction_id}</p>
                </div>
              </div>
            )}
          </div>

          {/* Method-specific Details */}
          {payment.payment_method === 'bank_transfer' && payment.bank_name && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 font-medium mb-1">Bank Name</p>
              <p className="text-sm text-gray-900 font-medium">{payment.bank_name}</p>
            </div>
          )}

          {payment.payment_method === 'cheque' && payment.cheque_number && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 font-medium mb-1">Cheque Number</p>
              <p className="text-sm text-gray-900 font-medium">{payment.cheque_number}</p>
            </div>
          )}

          {payment.payment_method === 'upi' && payment.upi_id && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 font-medium mb-1">UPI ID</p>
              <p className="text-sm text-gray-900 font-medium break-all">{payment.upi_id}</p>
            </div>
          )}

          {/* Remarks */}
          {payment.remarks && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 font-medium mb-1">Remarks</p>
              <p className="text-sm text-gray-900">{payment.remarks}</p>
            </div>
          )}

          {/* Receipt Section */}
          {payment.has_receipt && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-xs text-green-600 font-medium mb-2">Receipt</p>
              {payment.receipt_url && /\.(jpg|jpeg|png)$/i.test(payment.receipt_url) ? (
                <img
                  src={payment.receipt_url}
                  alt="Receipt"
                  className="w-full rounded-lg mb-2 border border-green-200 max-h-48 object-contain bg-white"
                />
              ) : (
                <p className="text-sm text-green-900 font-medium mb-2">Receipt has been attached</p>
              )}
              <Button variant="success" onClick={handleDownloadReceipt} icon={Download} size="sm">
                View / Download
              </Button>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-xs text-gray-600">
            <div className="flex justify-between">
              <span>Payment ID:</span>
              <span className="font-medium text-gray-900 break-all">{payment.id}</span>
            </div>
            <div className="flex justify-between">
              <span>Created:</span>
              <span className="font-medium text-gray-900">{formatDate(payment.created_at)} at {formatTime(payment.created_at)}</span>
            </div>
            {payment.updated_at && (
              <div className="flex justify-between">
                <span>Last Updated:</span>
                <span className="font-medium text-gray-900">{formatDate(payment.updated_at)} at {formatTime(payment.updated_at)}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="primary" onClick={() => { onEdit(); onClose(); }} className="flex-1">
              Edit Payment
            </Button>
            <Button variant="secondary" onClick={onClose} className="flex-1">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPaymentModal;
